import React, { useState } from 'react';
import * as registrationService from '../../services/registration/registrationService';

const RegistrationDebug = ({ eventId, userId, eventData, isDarkMode = false }) => {
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [registrationResult, setRegistrationResult] = useState(null);

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toISOString();
    setLogs(prev => [...prev, { timestamp, message, type }]);
    console.log(`[${type.toUpperCase()}] ${message}`);
  };

  const clearLogs = () => {
    setLogs([]);
    setRegistrationResult(null);
  };

  const testRegistrationCreation = async () => {
    setIsLoading(true);
    clearLogs();
    
    try {
      addLog('Starting registration creation test...', 'info');
      addLog(`Event ID: ${eventId}`, 'info');
      addLog(`User ID: ${userId}`, 'info');
      addLog(`Event Data: ${JSON.stringify(eventData, null, 2)}`, 'info');

      // Test 1: Check for existing registration
      addLog('Step 1: Checking for existing registration...', 'info');
      try {
        const existingReg = await registrationService.checkExistingRegistration(eventId, userId);
        if (existingReg) {
          addLog(`Found existing registration: ${existingReg.registrationId}`, 'warning');
          setRegistrationResult(existingReg);
        } else {
          addLog('No existing registration found', 'success');
        }
      } catch (error) {
        addLog(`Error checking existing registration: ${error.message}`, 'error');
      }

      // Test 2: Create new registration
      addLog('Step 2: Creating new registration...', 'info');
      try {
        const result = await registrationService.createNewRegistration(eventId, userId, eventData);
        addLog(`Registration creation result: ${JSON.stringify(result, null, 2)}`, 'success');
        setRegistrationResult(result);
        
        if (result && result.registrationId) {
          addLog(`✅ Registration created successfully with ID: ${result.registrationId}`, 'success');
        } else {
          addLog('❌ Registration creation failed - no registration ID returned', 'error');
        }
      } catch (error) {
        addLog(`❌ Registration creation failed: ${error.message}`, 'error');
        addLog(`Error details: ${JSON.stringify(error, null, 2)}`, 'error');
      }

      // Test 3: Verify registration was created
      if (registrationResult && registrationResult.registrationId) {
        addLog('Step 3: Verifying registration was created...', 'info');
        try {
          const verification = await registrationService.checkExistingRegistration(eventId, userId);
          if (verification && verification.registrationId === registrationResult.registrationId) {
            addLog('✅ Registration verification successful', 'success');
          } else {
            addLog('❌ Registration verification failed', 'error');
          }
        } catch (error) {
          addLog(`Error verifying registration: ${error.message}`, 'error');
        }
      }

    } catch (error) {
      addLog(`Unexpected error: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
      addLog('Registration test completed', 'info');
    }
  };

  const getLogColor = (type) => {
    switch (type) {
      case 'error': return isDarkMode ? 'text-red-400' : 'text-red-600';
      case 'warning': return isDarkMode ? 'text-yellow-400' : 'text-yellow-600';
      case 'success': return isDarkMode ? 'text-green-400' : 'text-green-600';
      default: return isDarkMode ? 'text-gray-300' : 'text-gray-700';
    }
  };

  return (
    <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
        Registration Flow Debug
      </h3>
      
      <div className="space-y-4">
        <div className="flex space-x-2">
          <button
            onClick={testRegistrationCreation}
            disabled={isLoading || !eventId || !userId}
            className={`px-4 py-2 rounded font-medium ${
              isLoading || !eventId || !userId
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : isDarkMode
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-blue-500 text-white hover:bg-blue-600'
            }`}
          >
            {isLoading ? 'Testing...' : 'Test Registration Creation'}
          </button>
          
          <button
            onClick={clearLogs}
            className={`px-4 py-2 rounded font-medium ${
              isDarkMode
                ? 'bg-gray-600 text-white hover:bg-gray-700'
                : 'bg-gray-500 text-white hover:bg-gray-600'
            }`}
          >
            Clear Logs
          </button>
        </div>

        {(!eventId || !userId) && (
          <div className={`p-3 rounded ${isDarkMode ? 'bg-yellow-900/20 text-yellow-400' : 'bg-yellow-100 text-yellow-800'}`}>
            Missing required data: {!eventId && 'eventId'} {!userId && 'userId'}
          </div>
        )}

        {registrationResult && (
          <div className={`p-4 rounded border ${isDarkMode ? 'bg-green-900/20 border-green-800' : 'bg-green-50 border-green-200'}`}>
            <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-green-400' : 'text-green-800'}`}>
              Registration Result:
            </h4>
            <pre className={`text-sm overflow-x-auto ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
              {JSON.stringify(registrationResult, null, 2)}
            </pre>
          </div>
        )}

        <div className={`border rounded p-4 max-h-96 overflow-y-auto ${isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50'}`}>
          <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Debug Logs:
          </h4>
          {logs.length === 0 ? (
            <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
              No logs yet. Click "Test Registration Creation" to start.
            </p>
          ) : (
            <div className="space-y-1">
              {logs.map((log, index) => (
                <div key={index} className="text-sm font-mono">
                  <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>
                    [{log.timestamp.split('T')[1].split('.')[0]}]
                  </span>
                  <span className={`ml-2 ${getLogColor(log.type)}`}>
                    {log.message}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegistrationDebug;